// Presentation → CSS declarations (pure, framework-free). Only TYPED values of
// the canonical document are mapped (colors already validated by the engine's
// pattern, numbers already bounded); no CSS string from the document is ever
// passed through. Shared by the Studio (editor + preview) and the served
// runtime's DOM renderer (RUNTIME-CONTRACT §6) since 7.6H: one mapping.

import type { ArtifactLayoutV1, Element, Fill } from "@interactads/creative-model";

/** Resolved presentation of one element in one variant (what the artifact carries). */
export type ElementPresentation = ArtifactLayoutV1["elements"][string];

/** Fallback font stack of every text layer (and of document fonts that fail to load in time). */
export const DEFAULT_FONT_STACK =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif';

export type Css = Record<string, string | number>;

type Shadow = { x: number; y: number; blur: number; spread?: number; color: string };
interface Box {
  fill?: Fill;
  border?: { width: number; color: string; style: string };
  radius?: number | readonly number[];
  shadows?: readonly Shadow[];
  blendMode?: string;
}
interface Typo {
  size: number;
  font?: string;
  weight?: number;
  italic?: boolean;
  lineHeight?: number;
  letterSpacing?: number;
  align?: "left" | "center" | "right" | "justify";
  verticalAlign?: "top" | "middle" | "bottom";
  color?: string;
  transform?: "none" | "uppercase" | "lowercase";
}

export function fillCss(fill: Fill | undefined): string {
  if (!fill || fill.kind === "none") return "transparent";
  if (fill.kind === "solid") return fill.color;
  const stops = fill.stops.map((s) => `${s.color} ${String(Math.round(s.at * 10000) / 100)}%`).join(", ");
  return fill.kind === "linear"
    ? `linear-gradient(${String(fill.angle ?? 180)}deg, ${stops})`
    : `radial-gradient(circle, ${stops})`;
}

function boxCss(style: Box | undefined, ellipse: boolean): Css {
  const css: Css = { background: fillCss(style?.fill), boxSizing: "border-box" };
  if (style?.border && style.border.width > 0)
    css.border = `${String(style.border.width)}px ${style.border.style} ${style.border.color}`;
  if (ellipse) css.borderRadius = "50%";
  else if (typeof style?.radius === "number") css.borderRadius = `${String(style.radius)}px`;
  else if (Array.isArray(style?.radius)) css.borderRadius = style.radius.map((r) => `${String(r)}px`).join(" ");
  if (style?.shadows && style.shadows.length > 0)
    css.boxShadow = style.shadows
      .map((s) => `${String(s.x)}px ${String(s.y)}px ${String(s.blur)}px ${String(s.spread ?? 0)}px ${s.color}`)
      .join(", ");
  if (style?.blendMode && style.blendMode !== "normal") css.mixBlendMode = style.blendMode;
  return css;
}

/** Font family CSS for a document font key (loaded as a FontFace under a namespaced family). */
export const fontFamilyFor = (fontKey: string | undefined, loaded: ReadonlySet<string>) =>
  fontKey && loaded.has(fontKey) ? `"ia-font-${fontKey}", ${DEFAULT_FONT_STACK}` : DEFAULT_FONT_STACK;

export function typographyCss(t: Typo | undefined, loadedFonts: ReadonlySet<string>): Css {
  if (!t) return {};
  const justify = { top: "flex-start", middle: "center", bottom: "flex-end" } as const;
  return {
    display: "flex",
    flexDirection: "column",
    justifyContent: justify[t.verticalAlign ?? "top"],
    fontFamily: fontFamilyFor(t.font, loadedFonts),
    fontSize: `${String(t.size)}px`,
    fontWeight: t.weight ?? 400,
    fontStyle: t.italic ? "italic" : "normal",
    lineHeight: t.lineHeight ?? 1.2,
    letterSpacing: `${String(t.letterSpacing ?? 0)}px`,
    textAlign: t.align ?? "left",
    // Justified text: the last line aligns to the start (fixed, documented in CREATIVE-MODEL §17.I).
    ...(t.align === "justify" ? { textAlignLast: "start" } : {}),
    color: t.color ?? "#000000",
    textTransform: t.transform ?? "none",
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    overflow: "hidden",
  };
}

/** Absolute box of an element in creative pixels (the artboard is scaled as a whole by the zoom). */
export function frameCss(p: ElementPresentation): Css {
  const f = p.frame as unknown as {
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
    origin?: { x: number; y: number };
  };
  const css: Css = {
    position: "absolute",
    left: `${String(f.x)}px`,
    top: `${String(f.y)}px`,
    width: `${String(f.width)}px`,
    height: `${String(f.height)}px`,
  };
  if (f.rotation) {
    css.transform = `rotate(${String(f.rotation)}deg)`;
    css.transformOrigin = `${String((f.origin?.x ?? 0.5) * 100)}% ${String((f.origin?.y ?? 0.5) * 100)}%`;
  }
  return css;
}

/** Full static (initial-state) style of an element in one variant. */
export function elementCss(el: Element, p: ElementPresentation, loadedFonts: ReadonlySet<string>): Css {
  const style = p.style as Box | undefined;
  const css: Css = { ...frameCss(p), opacity: el.opacity ?? 1 };
  switch (el.type) {
    case "shape":
      // Vector shapes draw their own SVG (shapeGeometry); the box itself stays transparent.
      return VECTOR_SHAPES.has(el.shape)
        ? { ...css, background: "transparent", overflow: "visible" }
        : { ...css, ...boxCss(style, el.shape === "ellipse") };
    case "group":
      // `clip` (CREATIVE-MODEL §5): children never draw outside the group's frame.
      return { ...css, ...boxCss(style, false), ...(el.clip ? { overflow: "hidden" } : {}) };
    case "button":
      return {
        ...css,
        ...boxCss(style, false),
        ...typographyCss(p.typography as Typo | undefined, loadedFonts),
        padding: "0 8px",
      };
    case "text":
      return { ...css, ...boxCss(style, false), ...typographyCss(p.typography as Typo | undefined, loadedFonts) };
    case "image":
      return { ...css, ...boxCss(style, false), overflow: "hidden" };
    default:
      return { ...css, ...boxCss(style, false) };
  }
}

const FOCAL = (v: number | undefined) => `${String((v ?? 0.5) * 100)}%`;

export function imageCss(el: Extract<Element, { type: "image" }>, p: ElementPresentation): Css {
  return {
    width: "100%",
    height: "100%",
    objectFit: (p.fit as string | undefined) ?? el.fit,
    objectPosition: `${FOCAL(el.focal?.x)} ${FOCAL(el.focal?.y)}`,
    display: "block",
    userSelect: "none",
    pointerEvents: "none",
  };
}

// ─── Vector shapes (7.6I) ──────────────────────────────────────────────────

const VECTOR_SHAPES: ReadonlySet<string> = new Set(["line", "polygon", "path"]);

/**
 * SVG geometry of a `line` / `polygon` / `path` shape, in the element's LOCAL
 * coordinates (px from the frame's top-left; viewBox = the frame), so it is
 * drawn inside the box that animations, states and the runtime position move.
 * Only typed values: points are bounded numbers and `path` already passed the
 * validator's restricted grammar (M L H V C S Q T A Z and numbers) — it is set
 * as an attribute of an SVG <path>, never parsed as markup. Shared by the
 * Studio (React) and the served DOM renderer. null = not a vector shape.
 */
export interface ShapeGeometry {
  readonly viewBox: string;
  readonly tag: "polyline" | "polygon" | "path";
  /** `points` (polyline/polygon) or `d` (path). */
  readonly attr: "points" | "d";
  readonly value: string;
  readonly fill: string;
  readonly stroke: string;
  readonly strokeWidth: number;
}

export function shapeGeometry(el: Element, p: ElementPresentation): ShapeGeometry | null {
  if (el.type !== "shape" || !VECTOR_SHAPES.has(el.shape)) return null;
  const f = p.frame as unknown as { width: number; height: number };
  const style = p.style as Box | undefined;
  const stroke = el.stroke ?? (el.shape === "line" ? { width: 1, color: "#000000" } : undefined);
  const pts = (el.points ?? []).map((pt) => `${String(pt.x)},${String(pt.y)}`).join(" ");
  const fill = el.shape === "line" ? "none" : fillSvg(style?.fill);
  const base = {
    viewBox: `0 0 ${String(f.width)} ${String(f.height)}`,
    fill,
    stroke: stroke?.color ?? "none",
    strokeWidth: stroke?.width ?? 0,
  };
  if (el.shape === "path") return el.path ? { ...base, tag: "path", attr: "d", value: el.path } : null;
  if (pts === "") return null;
  return { ...base, tag: el.shape === "line" ? "polyline" : "polygon", attr: "points", value: pts };
}

/** SVG paint of a fill: solid colors only (a gradient fill on a vector shape uses its first stop). */
function fillSvg(fill: Fill | undefined): string {
  if (!fill || fill.kind === "none") return "none";
  if (fill.kind === "solid") return fill.color;
  return fill.stops[0]?.color ?? "none";
}
